import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "assets/theme-dark/base/colors";
import { motion } from "framer-motion";
import { useState } from "react";

interface SheetSelectorDialogProps {
  open: boolean;
  sheetNames: string[];
  onClose: () => void;
  onSelect: (sheetName: string | null) => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: `linear-gradient(145deg, ${colors.background.card} 0%, ${colors.background.default} 100%)`,
    borderRadius: "12px",
    color: colors.text.main,
    minWidth: "320px",
    maxWidth: "480px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2)",
    overflow: "hidden",
    transition: "all 0.3s ease",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: colors.background.default,
  color: colors.text.main,
  fontWeight: "bold",
  padding: "16px 24px",
  display: "flex",
  alignItems: "center",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "20px 24px",
  background: colors.background.card,
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: "16px 24px",
  background: colors.background.default,
  display: "flex",
  gap: "12px",
  justifyContent: "flex-end",
}));

export function SheetSelectorDialog({ open, sheetNames, onClose, onSelect }: SheetSelectorDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");

  return (
    <StyledDialog open={open} onClose={onClose} aria-labelledby="sheet-selector-dialog-title" component={motion.div}>
      <StyledDialogTitle id="sheet-selector-dialog-title">
        <MDTypography variant="h6" fontWeight="bold" color="text">
          Select Sheet
        </MDTypography>
      </StyledDialogTitle>
      <StyledDialogContent>
        <Autocomplete
          options={sheetNames}
          onChange={(_, newValue) => onSelect(newValue)}
          onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Sheet"
              variant="outlined"
              size="small"
              sx={{
                width: "300px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: colors.background.default,
                  "& fieldset": {
                    borderColor: colors.grey[500],
                  },
                  "&:hover fieldset": {
                    borderColor: colors.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary.main,
                  },
                  color: colors.text.main,
                },
                "& .MuiInputBase-input": {
                  color: colors.text.main,
                },
                "& .MuiInputLabel-root": {
                  color: colors.grey[900],
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: colors.primary.main,
                },
              }}
              InputProps={{
                ...params.InputProps,
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <li key={key} {...otherProps}>
                <MDTypography variant="body2" color="text" sx={{ color: colors.text.main }}>
                  {option}
                </MDTypography>
              </li>
            );
          }}
          sx={{
            mt: 2,
            minWidth: 300,
            "& .MuiAutocomplete-option": {
              color: colors.text.main,
              "&:hover": {
                backgroundColor: colors.grey[800],
              },
            },
          }}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <MDButton
          onClick={onClose}
          variant="outlined"
          sx={{
            color: colors.text.main,
            borderColor: colors.grey[700],
            borderWidth: "1px",
            borderStyle: "solid",
            padding: "8px 16px",
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: colors.grey[300],
              borderColor: colors.grey[600],
              transform: "translateY(-1px)",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: "none",
            },
          }}
        >
          Cancel
        </MDButton>
      </StyledDialogActions>
    </StyledDialog>
  );
}
